/* TechnicianVisitDetail — iter97.88 (Phase A3)
 *
 * Single-visit drill-down where the technician captures scans + photos
 * on-site and marks the visit complete. The complete-visit action bumps
 * the linked case to `scan_visit_completed` so the doctor's dashboard
 * (A4) can surface a "scans uploaded — resume your case" nudge.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowLeft, Calendar, MapPin, Stethoscope, User, Phone, CheckCircle,
    UploadSimple, Trash, FileArrowUp, Camera, Cube, Image as ImageIcon, Spinner,
} from "@phosphor-icons/react";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";

/* Scan + photo slots the technician captures. Mirrors the case wizard
   so the doctor's resume flow sees identical record shapes. */
const SCAN_SLOTS = [
    { key: "scan_upper",  label: "Upper arch scan",   icon: Cube,   accept: ".stl,.ply,.obj,.zip,application/octet-stream" },
    { key: "scan_lower",  label: "Lower arch scan",   icon: Cube,   accept: ".stl,.ply,.obj,.zip,application/octet-stream" },
    { key: "scan_bite",   label: "Bite registration", icon: Cube,   accept: ".stl,.ply,.obj,.zip,application/octet-stream" },
];

const PHOTO_SLOTS = [
    { key: "photo_front",            label: "Face — front", icon: Camera, accept: "image/*" },
    { key: "photo_smile",            label: "Smile",        icon: Camera, accept: "image/*" },
    { key: "photo_profile",          label: "Profile",      icon: Camera, accept: "image/*" },
    { key: "photo_intraoral_upper",  label: "Upper occlusal",  icon: ImageIcon, accept: "image/*" },
    { key: "photo_intraoral_lower",  label: "Lower occlusal",  icon: ImageIcon, accept: "image/*" },
    { key: "photo_intraoral_front",  label: "Intraoral — front", icon: ImageIcon, accept: "image/*" },
    { key: "photo_intraoral_left",   label: "Buccal left",     icon: ImageIcon, accept: "image/*" },
    { key: "photo_intraoral_right",  label: "Buccal right",    icon: ImageIcon, accept: "image/*" },
];

export default function TechnicianVisitDetail() {
    const { visitId } = useParams();
    const navigate = useNavigate();
    const [visit, setVisit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState({}); // { slotKey: bool }
    const [completing, setCompleting] = useState(false);
    /* iter98.99i — Premium "Visit completed · records uploaded"
     * confirmation dialog. Replaces the thin toast and gives the
     * technician a polished moment of closure before returning to the
     * dashboard. Symmetric pair to the doctor-side `scan_visit_booked`
     * we shipped in iter98.99g. */
    const [completedDialogOpen, setCompletedDialogOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        api.get(`/coe/technician/visits/${visitId}`)
            .then((r) => setVisit(r.data))
            .catch((err) => setError(err.response?.data?.detail || "Could not load visit"))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [visitId]);

    const uploadedByKind = (visit?.uploaded_files || []).reduce((acc, f) => {
        acc[f.kind] = f;
        return acc;
    }, {});

    const handleFile = async (kind, file) => {
        if (!file) return;
        setUploading((s) => ({ ...s, [kind]: true }));
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/coe/technician/visits/${visitId}/upload`, fd, {
                params: { kind },
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${file.name} uploaded`);
            load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" && detail ? detail : "Upload failed");
        } finally {
            setUploading((s) => ({ ...s, [kind]: false }));
        }
    };

    const handleDelete = async (fileId) => {
        try {
            await api.delete(`/coe/technician/visits/${visitId}/files/${fileId}`);
            toast.success("Removed");
            load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" && detail ? detail : "Could not remove file");
        }
    };

    const handleComplete = async () => {
        if (completing) return;
        setCompleting(true);
        try {
            await api.post(`/coe/technician/visits/${visitId}/complete`, { notes });
            // iter98.99i — Replace the thin toast with the premium
            // CaseSubmittedDialog; navigation is deferred to dismissal so
            // the technician can read the celebration before being moved.
            setCompletedDialogOpen(true);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" && detail ? detail : "Could not complete visit");
        } finally {
            setCompleting(false);
        }
    };

    if (loading) return <div className="p-10 text-center text-muted-foreground">Loading visit…</div>;
    if (error || !visit) return (
        <div className="p-10 text-center text-muted-foreground" data-testid="technician-visit-error">
            {error || "Visit not found."}
            <div className="mt-4">
                <Link to="/coe/technician" className="text-primary font-semibold hover:underline">← Back to dashboard</Link>
            </div>
        </div>
    );

    const isCompleted = visit.status === "completed";
    const totalSlots = SCAN_SLOTS.length + PHOTO_SLOTS.length;
    const filledSlots = [...SCAN_SLOTS, ...PHOTO_SLOTS].filter((s) => uploadedByKind[s.key]).length;
    const canComplete = filledSlots > 0 && !isCompleted;

    return (
        <div data-testid="technician-visit-detail">
            <Link to="/coe/technician" className="text-xs text-primary inline-flex items-center gap-1 hover:underline mb-3" data-testid="technician-visit-back">
                <ArrowLeft size={12} /> Back to dashboard
            </Link>

            {/* Header */}
            <div className="bg-card border border-border rounded-2xl p-5 mb-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{visit.date} · {visit.time}</span>
                            {isCompleted ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
                                    <CheckCircle size={10} weight="fill" /> Completed
                                </span>
                            ) : (
                                <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">Assigned</span>
                            )}
                        </div>
                        <h1 className="font-display text-2xl font-bold">{visit.patient_name || "Patient"}</h1>
                        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                            <Phone size={13} /> {visit.patient_phone}
                            {visit.patient_gender ? <><span>·</span><span>{visit.patient_gender}</span></> : null}
                            {visit.patient_dob ? <><span>·</span><span>DOB {visit.patient_dob}</span></> : null}
                        </div>
                    </div>
                    {visit.case_code && (
                        <Link to={`/admin/cases/${visit.case_id}`} className="text-xs text-primary font-semibold hover:underline shrink-0">
                            {visit.case_code} →
                        </Link>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 text-sm">
                    <div className="bg-secondary/40 rounded-xl px-3 py-2.5">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-0.5">Provider</div>
                        <div className="font-medium flex items-center gap-1.5"><Stethoscope size={13} /> {visit.doctor_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{visit.doctor_phone || ""}</div>
                    </div>
                    <div className="bg-secondary/40 rounded-xl px-3 py-2.5">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-0.5">Clinic</div>
                        <div className="font-medium flex items-center gap-1.5"><MapPin size={13} /> {visit.doctor_clinic_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{visit.doctor_clinic_address || visit.doctor_clinic_city || ""}</div>
                    </div>
                </div>
                {visit.notes && (
                    <div className="mt-3 text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                        <span className="font-semibold text-amber-900">Doctor note:</span> {visit.notes}
                    </div>
                )}
            </div>

            {/* Progress chip */}
            <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-sm">
                    <span className="font-semibold">{filledSlots}</span>
                    <span className="text-muted-foreground"> of {totalSlots} slots captured</span>
                </div>
                {!isCompleted && (
                    <Button
                        onClick={handleComplete}
                        disabled={!canComplete || completing}
                        data-testid="technician-visit-complete-btn"
                    >
                        {completing ? "Completing…" : <><CheckCircle size={14} weight="bold" className="mr-1.5" /> Complete visit</>}
                    </Button>
                )}
            </div>

            {/* Scans */}
            <SlotGroup
                title="Intraoral scans"
                sub="STL / PLY / OBJ uploads from your scanner"
                slots={SCAN_SLOTS}
                uploadedByKind={uploadedByKind}
                uploading={uploading}
                isReadOnly={isCompleted}
                onFile={handleFile}
                onDelete={handleDelete}
            />

            {/* Photos */}
            <SlotGroup
                title="Clinical photographs"
                sub="Face + intraoral angles. JPEG / PNG / WebP."
                slots={PHOTO_SLOTS}
                uploadedByKind={uploadedByKind}
                uploading={uploading}
                isReadOnly={isCompleted}
                onFile={handleFile}
                onDelete={handleDelete}
            />

            {/* Completion notes */}
            {!isCompleted && (
                <div className="bg-card border border-border rounded-2xl p-5 mt-4" data-testid="technician-visit-notes-block">
                    <h3 className="font-semibold text-sm mb-2">Completion notes (optional)</h3>
                    <p className="text-xs text-muted-foreground mb-2">
                        Anything the planner / doctor should know? Bite issues, missed slots, patient comments, etc.
                    </p>
                    <Textarea
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="e.g. Couldn't capture distal #17 due to gag reflex. Patient consented to retake next visit."
                        data-testid="technician-visit-notes"
                    />
                </div>
            )}

            {isCompleted && visit.completion_notes && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mt-4 text-sm">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-800 mb-1">Completion notes</div>
                    {visit.completion_notes}
                </div>
            )}

            {/* iter98.99i — Premium "Visit completed · records uploaded"
                confirmation. Dismissal returns the technician to the
                visits dashboard so they can pick the next site. */}
            <CaseSubmittedDialog
                open={completedDialogOpen}
                flow="scan_visit_completed"
                caseCode={visit?.case_code || ""}
                primaryLabel="Back to visits"
                onPrimary={() => {
                    setCompletedDialogOpen(false);
                    navigate("/coe/technician");
                }}
            />
        </div>
    );
}

function SlotGroup({ title, sub, slots, uploadedByKind, uploading, isReadOnly, onFile, onDelete }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5 mb-3">
            <div className="mb-3">
                <h3 className="font-semibold text-sm">{title}</h3>
                <p className="text-xs text-muted-foreground">{sub}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {slots.map((slot) => {
                    const f = uploadedByKind[slot.key];
                    const busy = uploading[slot.key];
                    return (
                        <SlotTile
                            key={slot.key}
                            slot={slot}
                            file={f}
                            busy={busy}
                            isReadOnly={isReadOnly}
                            onFile={(file) => onFile(slot.key, file)}
                            onDelete={() => onDelete(f.id)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function SlotTile({ slot, file, busy, isReadOnly, onFile, onDelete }) {
    const inputRef = useRef(null);
    const Icon = slot.icon;
    const hasFile = Boolean(file);
    return (
        <div
            className={`border rounded-xl p-3 transition-colors ${
                hasFile ? "border-emerald-300 bg-emerald-50/40" : "border-dashed border-border bg-secondary/20 hover:border-primary/40"
            }`}
            data-testid={`technician-slot-${slot.key}`}
        >
            <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className={hasFile ? "text-emerald-700" : "text-muted-foreground"} />
                <span className="text-xs font-semibold flex-1">{slot.label}</span>
                {hasFile && <CheckCircle size={14} weight="fill" className="text-emerald-700" />}
            </div>
            {hasFile ? (
                <div className="space-y-2">
                    <div className="text-[11px] text-muted-foreground truncate">{file.name}</div>
                    <div className="text-[10px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</div>
                    {!isReadOnly && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                className="text-[11px] text-primary font-semibold hover:underline"
                                data-testid={`technician-slot-${slot.key}-replace`}
                                disabled={busy}
                            >
                                Replace
                            </button>
                            <button
                                type="button"
                                onClick={onDelete}
                                className="text-[11px] text-rose-700 font-semibold hover:underline inline-flex items-center gap-1"
                                data-testid={`technician-slot-${slot.key}-delete`}
                                disabled={busy}
                            >
                                <Trash size={11} /> Remove
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={isReadOnly || busy}
                    className="w-full text-xs font-semibold text-primary py-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid={`technician-slot-${slot.key}-upload`}
                >
                    {busy ? <Spinner size={13} className="animate-spin" /> : <FileArrowUp size={13} />}
                    {busy ? "Uploading…" : "Choose file"}
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={slot.accept}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
            />
        </div>
    );
}
